import React, { useState } from 'react'
import { AlertTriangle, CheckCircle, Mic, Volume2, Wifi, RefreshCw } from 'lucide-react'
import ContactSupportModal from '../components/ContactSupportModal'

const TroubleshootingPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false)

  const commonIssues = [
    {
      title: 'Microphone Not Working',
      icon: Mic,
      color: 'from-red-500 to-pink-500',
      symptoms: [
        'AI can\'t hear me speaking',
        'No audio input detected',
        'Microphone permission denied'
      ],
      solutions: [
        'Check browser microphone permissions in settings',
        'Ensure microphone is not muted or blocked by other applications',
        'Try refreshing the page and allowing microphone access',
        'Test microphone with other applications to confirm it works',
        'For Windows: Check microphone privacy settings in Windows Settings'
      ]
    },
    {
      title: 'Audio Playback Issues',
      icon: Volume2,
      color: 'from-blue-500 to-cyan-500',
      symptoms: [
        'Can\'t hear AI responses',
        'Audio is choppy or distorted',
        'Volume is too low'
      ],
      solutions: [
        'Check your device volume and browser audio settings',
        'Try using headphones instead of speakers',
        'Disable browser extensions that might interfere with audio',
        'Check if other tabs are playing audio simultaneously',
        'Restart your browser and try again'
      ]
    },
    {
      title: 'Connection Problems',
      icon: Wifi,
      color: 'from-green-500 to-teal-500',
      symptoms: [
        'Slow AI responses',
        'Frequent disconnections',
        'Page fails to load properly'
      ],
      solutions: [
        'Check your internet connection speed',
        'Try switching to a different network or mobile data',
        'Close unnecessary browser tabs and applications',
        'Clear browser cache and cookies',
        'Restart your router if using Wi-Fi'
      ]
    },
    {
      title: 'Performance Issues',
      icon: RefreshCw,
      color: 'from-purple-500 to-pink-500',
      symptoms: [
        'App runs slowly',
        'Frequent freezing',
        'Long loading times'
      ],
      solutions: [
        'Close other resource-intensive applications',
        'Use a supported browser (Chrome, Firefox, Safari, Edge)',
        'Update your browser to the latest version',
        'Clear browser cache and restart',
        'Check if your device meets minimum requirements'
      ]
    }
  ]

  const quickFixes = [
    {
      title: 'Refresh the Page',
      description: 'Often solves temporary glitches and connection issues',
      action: 'Press F5 or Ctrl+R (Cmd+R on Mac)'
    },
    {
      title: 'Clear Browser Cache',
      description: 'Removes stored data that might cause conflicts',
      action: 'Browser Settings → Privacy/Security → Clear Browsing Data'
    },
    {
      title: 'Try Incognito/Private Mode',
      description: 'Bypasses extensions and cached data',
      action: 'Ctrl+Shift+N (Chrome) or Ctrl+Shift+P (Firefox)'
    },
    {
      title: 'Restart Browser',
      description: 'Clears memory and resets browser state',
      action: 'Close all browser windows and reopen'
    }
  ]

  const systemRequirements = {
    browsers: [
      'Chrome 80+ (Recommended)',
      'Firefox 75+',
      'Safari 13+',
      'Edge 80+'
    ],
    connection: [
      'Stable internet connection',
      'Minimum 1 Mbps upload speed',
      'Low latency (< 100ms recommended)'
    ],
    hardware: [
      'Working microphone',
      'Speakers or headphones',
      '2GB RAM minimum',
      'Modern processor (2015+)'
    ]
  }

  return (
    <div className="px-4 py-12 sm:px-6 lg:px-8 animate-fade-in">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            <span className="gradient-text">Troubleshooting</span> Guide
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Having technical issues? Find solutions to common problems and get back to learning English with AI.
          </p>
        </div>

        {/* Quick Fixes */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-8">Quick Fixes to Try First</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickFixes.map((fix, index) => (
              <div
                key={index}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-4 border border-slate-700 hover:border-slate-600 transition-all duration-300"
              >
                <h3 className="text-lg font-semibold text-white mb-2">{fix.title}</h3>
                <p className="text-slate-300 text-sm mb-3">{fix.description}</p>
                <code className="text-xs bg-slate-900 text-purple-400 px-2 py-1 rounded block">
                  {fix.action}
                </code>
              </div>
            ))}
          </div>
        </section>

        {/* Common Issues */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-8">Common Issues & Solutions</h2>
          <div className="space-y-8">
            {commonIssues.map((issue, index) => {
              const Icon = issue.icon
              return (
                <div
                  key={index}
                  className="bg-slate-800/30 rounded-xl p-6 border border-slate-700"
                >
                  <div className="flex items-center mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br ${issue.color} rounded-lg mr-4`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-2xl font-semibold text-white">{issue.title}</h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Symptoms */}
                    <div>
                      <h4 className="text-lg font-medium text-white mb-4 flex items-center">
                        <AlertTriangle className="h-5 w-5 text-orange-400 mr-2" />
                        Symptoms
                      </h4>
                      <ul className="space-y-2">
                        {issue.symptoms.map((symptom, symptomIndex) => (
                          <li key={symptomIndex} className="flex items-start text-slate-300">
                            <span className="w-2 h-2 bg-orange-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            {symptom}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Solutions */}
                    <div>
                      <h4 className="text-lg font-medium text-white mb-4 flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
                        Solutions
                      </h4>
                      <ol className="space-y-2">
                        {issue.solutions.map((solution, solutionIndex) => (
                          <li key={solutionIndex} className="flex items-start text-slate-300">
                            <span className="w-6 h-6 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center text-xs font-medium mt-0.5 mr-3 flex-shrink-0">
                              {solutionIndex + 1}
                            </span>
                            {solution}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* System Requirements */}
        <section className="mb-16">
          <h2 className="text-2xl font-semibold text-white mb-8">System Requirements</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Supported Browsers</h3>
              <ul className="space-y-2">
                {systemRequirements.browsers.map((browser, index) => (
                  <li key={index} className="flex items-center text-slate-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                    {browser}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Internet Connection</h3>
              <ul className="space-y-2">
                {systemRequirements.connection.map((req, index) => (
                  <li key={index} className="flex items-center text-slate-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-white mb-4">Hardware</h3>
              <ul className="space-y-2">
                {systemRequirements.hardware.map((hw, index) => (
                  <li key={index} className="flex items-center text-slate-300">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2" />
                    {hw}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Still Need Help */}
        <section className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl p-8 border border-blue-500/30 text-center">
          <h2 className="text-2xl font-semibold text-white mb-4">Still Need Help?</h2>
          <p className="text-slate-300 mb-6">
            If you've tried these solutions and still experiencing issues, our support team is here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
            >
              Contact Support
            </button>
            <a
              href="/faq"
              className="inline-flex items-center px-6 py-3 border border-slate-600 text-white font-semibold rounded-lg hover:bg-slate-800 transition-all duration-200"
            >
              Check FAQ
            </a>
          </div>
          <p className="text-slate-400 text-sm mt-4">
            When contacting support, please include your browser version, operating system, and a description of the issue.
          </p>
        </section>
      </div>

      <ContactSupportModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
      />
    </div>
  )
}

export default TroubleshootingPage